import { AppDispatch } from "../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../store/application/slice";
import {
  applyForAJobThunk,
  getCandidateAcceptedListings,
  getCandidateHistoryListings,
  getCandidateJobListings,
  getCandidatePendingListings,
} from "../store/candidate/thunks";
import { TJobData } from "../store/candidate/types";
import { TCandidateProfileStesps } from "../store/onboarding/types";


interface FetchJobListParams {
  page: number;
  per_page: number;
  sort_order: string;
  sort_by: string;
  keyword?: string;
  positions?: number[];
  centre_ids?: number[];
  distance_away?: number;
}

/**
 * Fetches the pending job listings for a candidate.
 *
 * @param dispatch - Redux dispatch function.
 * @param params - Pagination and sorting parameters.
 */
export const fetchPendingJobs = async (
  dispatch: AppDispatch,
  params: FetchJobListParams
) => {
  dispatch(showTransparentPreloaderAction());
  const response = await dispatch(getCandidatePendingListings(params));
  dispatch(hideTransparentPreloaderAction());
  return response;
};

/**
 * Fetches the job listings for a candidate.
 *
 * @param dispatch - Redux dispatch function.
 * @param params - Pagination and sorting parameters.
 */
export const fetchJobListings = async (
  dispatch: AppDispatch,
  params: FetchJobListParams
) => {
  dispatch(showTransparentPreloaderAction());
  const response = await dispatch(getCandidateJobListings(params));
  dispatch(hideTransparentPreloaderAction());
  return response;
};

/**
 * Fetches the job listings for a candidate.
 *
 * @param dispatch - Redux dispatch function.
 * @param params - Pagination and sorting parameters.
 */
export const fetchAcceptedListings = async (
    dispatch: AppDispatch,
    params: FetchJobListParams
  ) => {
    dispatch(showTransparentPreloaderAction());
    const response = await dispatch(getCandidateAcceptedListings(params));
    dispatch(hideTransparentPreloaderAction());
    return response;
  };

  /**
 * Fetches the job listings for a candidate.
 *
 * @param dispatch - Redux dispatch function.
 * @param params - Pagination and sorting parameters.
 */
export const fetchHistoryListings = async (
    dispatch: AppDispatch,
    params: FetchJobListParams
  ) => {
    dispatch(showTransparentPreloaderAction());
    const response = await dispatch(getCandidateHistoryListings(params));
    dispatch(hideTransparentPreloaderAction());
    return response;
  };

  /**
 * Applies for a job.
 *
 * @param dispatch - Redux dispatch function.
 * @param vacancy - The vacancy object containing necessary details.
 */
export const applyForAJob = async (
  dispatch: AppDispatch,
  data: {action: string | null, uuid: string, candidate_uuid: string, selection_method: string, type?: string | null, isResponseTimeGotOver?: boolean }
) => {
  const payLoad = new FormData();
  if(data?.action){
    payLoad.append("action", data?.action);
  }
  payLoad.append("user_uuid", data?.candidate_uuid);
  payLoad.append("vacancy_uuid", data?.uuid);
  payLoad.append("comments", "");
  if(data?.type){
    payLoad.append("type", data?.type || "");
  }

  dispatch(showTransparentPreloaderAction());
  const response = await dispatch(applyForAJobThunk(payLoad));
  dispatch(hideTransparentPreloaderAction());

  return response;
};

  /**
 * withDraw Or Decline a Job by candidate.
 *
 * @param dispatch - Redux dispatch function.
 * @param vacancy - The vacancy object containing necessary details.
 */
export const withDrawOrDeclineJob = async (
  dispatch: AppDispatch,
  data: { uuid: string, candidate_uuid: string, action: string, comments: string }
) => {
  const payLoad = new FormData();
  payLoad.append("action", data.action);
  payLoad.append("user_uuid", data?.candidate_uuid);
  payLoad.append("vacancy_uuid", data?.uuid);
  payLoad.append("comments", data?.comments || "");
  payLoad.append("type", "");

  dispatch(showTransparentPreloaderAction());
  const response = await dispatch(applyForAJobThunk(payLoad));
  dispatch(hideTransparentPreloaderAction());

  return response;
};

export const getJobTitleNameForCandidate = (vacancy: TJobData) => {
  if(vacancy){
    if(vacancy?.vacancy_positions?.length > 0){
      return vacancy?.vacancy_positions[0]?.name;
    }
  }
}

export const getJobCentreNameByVacancy = (vacancy: TJobData) => {
  if(vacancy){
    if(vacancy?.centre){
      return vacancy?.centre.business_name
    }
  }
}

export const isThirdStageActivated = (profileSteps: TCandidateProfileStesps[]) => {
  if(!profileSteps) return false; 
  const stage3 = profileSteps.find((step) => step.step_number === 3);
  return stage3 && stage3.action === "activate";   
}

export const isStageOneActivated = (profileSteps: TCandidateProfileStesps[], stageNumber: number) => {
  if(!profileSteps) return false; 
  const stage = profileSteps.find((step) => step.step_number === stageNumber);
  return stage && (stage.action === "approve_candidate_to_stage_2" || stage.action === "approve_candidate_bypass_compliance_check");
}

export const isStageTwoActivated = (profileSteps: TCandidateProfileStesps[], stageNumber: number) => {
  if(!profileSteps) return false; 
  const stage = profileSteps.find((step) => step.step_number === stageNumber);
  return stage && (stage.action === "approve_candidate_to_stage_3");
}

interface Availability {
  candidate_id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
  all_day_available?: number;
  date?: string; // Optional date field for filtering past availabilities
}

/**
 * Filters out availabilities with dates in the past.
 * 
 * @param availabilities - Array of candidate availability objects
 * @returns Filtered array excluding past date availabilities
 */
const filterPastAvailabilities = (availabilities: Availability[]): Availability[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

  return availabilities.filter((avail) => {
    // If no date field, keep the availability (recurring/weekly availability)
    if (!avail.date) {
      return true;
    }
    
    const availDate = new Date(avail.date);
    availDate.setHours(0, 0, 0, 0);
    
    // Keep only current and future dates
    return availDate >= today;
  });
};

/**
 * Formats candidate availability display based on their schedule.
 * 
 * @param availabilities - Array of candidate availability objects or null
 * @returns Formatted availability string:
 *   - "Everyday All Day" if null/empty or if all_day_available is 1
 *   - "Everyday 9am to 5pm" if all 7 days have availability (all_day_available is 2)
 *   - "Mon 9am to 5pm + 2 others" if partial days have availability
 *   - Past date availabilities are excluded from consideration
 */
export const formatAvailabilityDisplay = (availabilities: Availability[] | null): string => {
  // Case 1: If null or empty, return default
  if (!availabilities || !Array.isArray(availabilities) || availabilities.length === 0) {
    return "Everyday All Day";
  }

  // Filter out past date availabilities
  const currentAvailabilities = filterPastAvailabilities(availabilities);

  // If all availabilities were in the past, return default
  if (currentAvailabilities.length === 0) {
    return "Everyday All Day";
  }

  // Case 2: If all availabilities have all_day_available === 0, return "Not Available"
  const allUnavailable = currentAvailabilities.every((avail) => avail.all_day_available === 0);
  if (allUnavailable) {
    return "Not Available";
  }

  // Separate availabilities by type
  const partialAvailabilities = currentAvailabilities.filter((avail) => avail.all_day_available === 2);
  const allDayAvailabilities = currentAvailabilities.filter((avail) => avail.all_day_available === 1);

  // If only all-day availability (no specific times), return default
  if (partialAvailabilities.length === 0) {
    return "Everyday All Day";
  }

  // Get unique days
  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  
  // Build map of days with specific times
  const partialDayMap = new Map<string, { start: string; end: string }[]>();
  for (const avail of partialAvailabilities) {
    if (!partialDayMap.has(avail.day_of_week)) {
      partialDayMap.set(avail.day_of_week, []);
    }
    partialDayMap.get(avail.day_of_week)!.push({
      start: avail.start_time,
      end: avail.end_time,
    });
  }

  // Get unique days in order
  const uniquePartialDays = daysOfWeek.filter((day) => partialDayMap.has(day));
  
  // Get unique all-day days
  const uniqueAllDayDays = new Set(allDayAvailabilities.map((avail) => avail.day_of_week));

  // Helper function to format time (convert 24h to 12h format)
  const formatTime = (time: string | null): string => {
    if (!time) return "00:00";
    const [hours, minutes] = time.split(":");
    const hour = Number.parseInt(hours, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    
    let displayHour: number;
    if (hour === 0) {
      displayHour = 12;
    } else if (hour > 12) {
      displayHour = hour - 12;
    } else {
      displayHour = hour;
    }
    
    const minutePart = minutes === "00" ? "" : `:${minutes}`;
    return `${displayHour}${minutePart}${ampm}`;
  };

  // Get the time range for the first day with specific times
  const firstDay = uniquePartialDays[0];
  const firstDaySlots = partialDayMap.get(firstDay)!;
  const earliestStart = firstDaySlots.reduce((min, slot) => (slot.start && slot.start < min) ? slot.start : min, firstDaySlots[0].start || "00:00");
  const latestEnd = firstDaySlots.reduce((max, slot) => (slot.end && slot.end > max) ? slot.end : max, firstDaySlots[0].end || "23:59");
  
  const timeRange = `${formatTime(earliestStart)} to ${formatTime(latestEnd)}`;

  // Check if it's "All Day" (e.g., 00:00 to 23:59 or similar)
  const isAllDay = earliestStart === "00:00" && (latestEnd === "23:59" || latestEnd === "24:00");
  const displayTime = isAllDay ? "All Day" : timeRange;

  // Calculate total unique days (partial days + all-day days)
  const totalUniqueDays = uniquePartialDays.length + uniqueAllDayDays.size;

  // Case 2: If all 7 days are present, return "Everyday"
  if (totalUniqueDays === 7) {
    return `Everyday ${displayTime}`;
  }

  // Case 3: If partial days, show first day + others
  const dayAbbr = firstDay.substring(0, 3); // Mon, Tue, etc.
  const othersCount = totalUniqueDays - 1;
  
  if (othersCount === 0) {
    return `${dayAbbr} ${displayTime}`;
  }
  
  return `${dayAbbr} ${displayTime} + ${othersCount} other${othersCount > 1 ? 's' : ''}`;
};
